import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { requestLoginCode, verifyLoginCode } from "../lib/api";
import { useAuth } from "../lib/auth";
import { InlineSpinner } from "../components/Loading";
import { Eyebrow, Wordmark } from "../components/design";

// /login/verify — Step 2 of the 6-digit email code login.
// Six single-digit inputs with paste support and auto-advance.

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

export default function LoginVerify() {
  const [params] = useSearchParams();
  const email = (params.get("email") || "").trim();
  const navigate = useNavigate();
  const { signInWithToken } = useAuth();

  const [digits, setDigits] = useState<string[]>(() =>
    Array(CODE_LENGTH).fill(""),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const submittedRef = useRef(false);

  // Bounce back to /login if no email param.
  useEffect(() => {
    if (!email) navigate("/login", { replace: true });
  }, [email, navigate]);

  // Resend countdown.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  // Focus the first empty box on mount.
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const code = useMemo(() => digits.join(""), [digits]);

  const submit = useCallback(
    async (full: string) => {
      if (busy || submittedRef.current) return;
      submittedRef.current = true;
      setBusy(true);
      setError(null);
      try {
        const { customToken } = await verifyLoginCode(email, full);
        await signInWithToken(customToken);
        navigate("/dashboard", { replace: true });
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "verify_failed";
        const human =
          message === "expired_code"
            ? "Der Code ist abgelaufen. Bitte einen neuen anfordern."
            : message === "invalid_code"
              ? "Der Code stimmt nicht. Bitte erneut prüfen."
              : "Anmeldung fehlgeschlagen.";
        setError(human);
        submittedRef.current = false;
        // Clear the inputs so the user can retype.
        setDigits(Array(CODE_LENGTH).fill(""));
        inputsRef.current[0]?.focus();
      } finally {
        setBusy(false);
      }
    },
    [busy, email, navigate, signInWithToken],
  );

  // Auto-submit once 6 digits are entered.
  useEffect(() => {
    if (code.length === CODE_LENGTH && /^\d{6}$/.test(code)) {
      void submit(code);
    }
  }, [code, submit]);

  function setDigitAt(i: number, value: string) {
    setError(null);
    setDigits((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  }

  function onChangeBox(i: number, raw: string) {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      setDigitAt(i, "");
      return;
    }
    // If the user pasted multiple digits, distribute them.
    if (cleaned.length > 1) {
      handlePaste(cleaned, i);
      return;
    }
    setDigitAt(i, cleaned[0]);
    if (i < CODE_LENGTH - 1) inputsRef.current[i + 1]?.focus();
  }

  function onKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
      setDigitAt(i - 1, "");
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && i > 0) {
      inputsRef.current[i - 1]?.focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && i < CODE_LENGTH - 1) {
      inputsRef.current[i + 1]?.focus();
      e.preventDefault();
    }
  }

  function handlePaste(pastedDigits: string, startIndex: number) {
    const cleaned = pastedDigits.replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!cleaned) return;
    setDigits((prev) => {
      const next = [...prev];
      for (let k = 0; k < cleaned.length && startIndex + k < CODE_LENGTH; k++) {
        next[startIndex + k] = cleaned[k];
      }
      return next;
    });
    const lastIdx = Math.min(startIndex + cleaned.length, CODE_LENGTH) - 1;
    inputsRef.current[Math.max(0, lastIdx)]?.focus();
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>, i: number) {
    const text = e.clipboardData.getData("text");
    if (/\d/.test(text)) {
      e.preventDefault();
      handlePaste(text, i);
    }
  }

  async function resend() {
    if (resendIn > 0 || busy) return;
    setError(null);
    setBusy(true);
    try {
      await requestLoginCode(email);
      setResendIn(RESEND_SECONDS);
      setDigits(Array(CODE_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Etwas ist schiefgelaufen.";
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="paper-bg"
      style={{
        minHeight: "100vh",
        fontFamily: "var(--f-body)",
        color: "var(--ink)",
      }}
    >
      <div
        style={{
          maxWidth: 460,
          margin: "0 auto",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "60px 22px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link to="/" aria-label="Vitanara Startseite">
            <Wordmark />
          </Link>
          <div
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 12,
              color: "var(--ink-faint)",
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              fontWeight: 600,
            }}
          >
            Mein Buch
          </div>
        </header>

        <main
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px 22px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 420 }}>
            <Eyebrow>Schritt 2</Eyebrow>
            <h1
              style={{
                fontFamily: "var(--f-display)",
                fontWeight: 400,
                fontSize: 36,
                lineHeight: 1.05,
                letterSpacing: "-0.015em",
                margin: "0 0 14px",
                color: "var(--ink)",
              }}
            >
              Geben Sie den
              <br />
              <em>Code ein</em>.
            </h1>
            <p
              style={{
                fontFamily: "var(--f-body)",
                fontSize: 16,
                lineHeight: 1.45,
                color: "var(--ink-soft)",
                margin: "0 0 28px",
              }}
            >
              Wir haben eine E-Mail an{" "}
              <strong style={{ color: "var(--ink)" }}>{email}</strong>{" "}
              geschickt.
            </p>

            <div
              role="group"
              aria-label="6-stelliger Anmeldecode"
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "space-between",
                marginBottom: 18,
              }}
            >
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={CODE_LENGTH}
                  value={d}
                  onChange={(e) => onChangeBox(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  onPaste={(e) => onPaste(e, i)}
                  aria-label={`Ziffer ${i + 1}`}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    aspectRatio: "1 / 1.2",
                    background: "var(--paper-50)",
                    border: d
                      ? "1px solid rgba(43,26,15,0.18)"
                      : "1.5px solid rgba(43,26,15,0.14)",
                    borderRadius: 10,
                    textAlign: "center",
                    fontFamily: "var(--f-display)",
                    fontSize: 28,
                    color: "var(--ink)",
                    outline: "none",
                  }}
                />
              ))}
            </div>

            {error && (
              <p
                role="alert"
                style={{
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "var(--oxblood)",
                  margin: "0 0 16px",
                  textAlign: "center",
                }}
              >
                {error}
              </p>
            )}

            <div
              style={{
                background: "rgba(168,122,58,0.12)",
                borderLeft: "3px solid var(--ochre)",
                borderRadius: 4,
                padding: "12px 14px",
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 14,
                lineHeight: 1.45,
                color: "var(--ink-soft)",
                marginBottom: 18,
              }}
            >
              Aus Sicherheit ist der Code 10 Minuten gültig.
            </div>

            {busy && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "0 0 12px",
                }}
              >
                <InlineSpinner />
              </div>
            )}

            <div
              style={{
                textAlign: "center",
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--ink-faint)",
              }}
            >
              Keine E-Mail erhalten?{" "}
              {resendIn > 0 ? (
                <span style={{ color: "var(--ink-soft)" }}>
                  In {String(Math.floor(resendIn / 60)).padStart(1, "0")}:
                  {String(resendIn % 60).padStart(2, "0")} erneut senden
                </span>
              ) : (
                <button
                  type="button"
                  onClick={resend}
                  disabled={busy}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    color: "var(--oxblood)",
                    fontStyle: "normal",
                    fontWeight: 600,
                    cursor: busy ? "default" : "pointer",
                    fontSize: 13,
                    fontFamily: "var(--f-ui)",
                  }}
                >
                  Code erneut senden
                </button>
              )}
            </div>

            <p
              style={{
                marginTop: 14,
                textAlign: "center",
                fontFamily: "var(--f-ui)",
                fontSize: 12,
                color: "var(--ink-faint)",
              }}
            >
              <Link to="/login" style={{ color: "inherit" }}>
                Andere E-Mail-Adresse verwenden
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
